import { type FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { Box, Divider, Stack, Text } from '@eneco/sparky';

import { DetailValueRow } from './PriceDetails.DetailValueRow';
import { InfoPopover } from './PriceDetails.InfoPopover';
import { type RowProps } from './PriceDetails.types';
import { formatFormattedValue, resolveTypeIcon } from './PriceDetails.utils';

export const Row: FC<RowProps> = ({
  title,
  text,
  label,
  id,
  value,
  highlightText,
  highlightValue,
  highlightDivider,
  showDivider,
  isNested,
  popoverContent,
  type,
}) => {
  const { currency } = useFormatter();
  const icon = resolveTypeIcon(type);
  const formatValue = (amount: string | number) => formatFormattedValue(currency, amount);

  return (
    <>
      {!isNested && showDivider && <Divider isDecorative={true} emphasis={highlightDivider ? 'high' : 'low'} />}

      <Box paddingY="3" paddingLeft="3" paddingRight={isNested ? '3' : '0'}>
        <Text size={isNested ? 'BodyS' : { initial: 'BodyS', md: 'BodyM' }}>
          {title && <Stack.Item>{title}</Stack.Item>}
          {isNested ? (
            <DetailValueRow
              text={text}
              value={value}
              highlightText={highlightText}
              formatValue={formatValue}
              icon={icon ? <Box>{icon}</Box> : undefined}
            />
          ) : (
            <Stack direction="row" alignY="start" gap="4">
              {(text || label) && (
                <Stack.Item grow={typeof value !== 'string'}>
                  <Stack direction="row" alignY="center" gap="2">
                    <Text id={id}>
                      {label && <Text weight="bold">{label} </Text>}
                      <Text weight={highlightText ? 'bold' : 'regular'}>
                        <RichText html={text} />
                      </Text>
                    </Text>
                    {popoverContent && (
                      <InfoPopover title={popoverContent} content={<RichText html={popoverContent} />} />
                    )}
                  </Stack>
                </Stack.Item>
              )}

              {(value === 0 || value) && (
                <Stack.Item shrink={false} grow={typeof value === 'string'}>
                  <Text weight={highlightValue ? 'bold' : 'regular'} align="right">
                    {formatValue(value)}
                  </Text>
                </Stack.Item>
              )}
            </Stack>
          )}
        </Text>
      </Box>
    </>
  );
};
